import React, { useState, useCallback } from "react";
import { Grid, Card, Typography, Tabs, Tab, Box } from "@mui/material";
import SoftBox from "components/SoftBox";
import axios from "../../api";
import CustomerProfile from "./CustomerProfile";
import ViewAddresses from "./ViewAddresses";
import ViewAssets from "./ViewAssets";
import ViewSubscription from "./ViewSubscriptions";
import ViewDivisions from "./ViewDivisions";
import ViewMembers from "./ViewMembers";
import { CustomerIdContext } from "context/CustomerIdContext";

interface Customer {
  name: string;
  type: string;
}

interface CustomerDetailContentProps {
  /** Customer id to load and display (from route or overlay) */
  customerId: string | undefined;
}

/**
 * Reusable customer detail with tabs (Profile, Addresses, Assets, etc.).
 * Used both on the View Customer page and in the Admin Tickets customer overlay.
 * When used in overlay, wrap in CustomerIdContext.Provider so tab content can resolve id.
 */
const CustomerDetailContent = ({ customerId }: CustomerDetailContentProps) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [customer, setCustomer] = useState<Customer | null>(null);

  const handleTabChange = (_: any, newValue: number) => {
    setTabIndex(newValue);
  };

  const fetchCustomer = useCallback(async () => {
    if (!customerId) return;
    try {
      const response = await axios.get(`/customer/${customerId}`);
      setCustomer(response?.data || null);
      setTabIndex(0);
    } catch (e) {
      console.error(e);
    }
  }, [customerId]);

  React.useEffect(() => {
    fetchCustomer();
  }, [fetchCustomer]);

  const tabs = [
    { label: "Profile", component: <CustomerProfile customer={customer} /> },
  ];
  if (customer?.type === "company") {
    tabs.push(
      { label: "Divisions", component: <ViewDivisions /> },
      { label: "Members", component: <ViewMembers /> }
    );
  }
  tabs.push(
    { label: "Addresses", component: <ViewAddresses /> },
    { label: "Assets", component: <ViewAssets /> },
    { label: "Subscriptions", component: <ViewSubscription /> }
  );

  if (!customerId) {
    return null;
  }

  const displayName =
    customer?.name != null && String(customer.name).trim() !== ""
      ? String(customer.name)
      : "Customer";

  return (
    <CustomerIdContext.Provider value={Number(customerId)}>
      <Card
        sx={{
          maxWidth: "100%",
          width: "100%",
          padding: "24px",
          boxShadow: "none",
          minHeight: 0,
          overflow: "visible",
        }}
      >
        <SoftBox mt={2}>
          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            marginBottom={2}
          >
            <Grid item>
              <Typography variant="h5" component="div" gutterBottom>
                {displayName}'s Profile
              </Typography>
            </Grid>
          </Grid>
          <Box className="profile-tabs">
            <Tabs value={tabIndex} onChange={handleTabChange}>
              {tabs.map((t) => (
                <Tab key={t.label} label={t.label} />
              ))}
            </Tabs>
          </Box>

          <Box mt={2}>{tabs[tabIndex]?.component}</Box>
        </SoftBox>
      </Card>
    </CustomerIdContext.Provider>
  );
};

export default CustomerDetailContent;
